package it.unibo.tuprolog.solve.libs.oop.rules

import it.unibo.tuprolog.core.Scope
import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.solve.ExecutionContext
import it.unibo.tuprolog.solve.libs.oop.primitives.InvokeMethod
import it.unibo.tuprolog.solve.rule.RuleWrapper

/**
 * `property_reduce(+Expression, ?Ref, ?Property)`: splits a fluent chain `Expression` (see
 * [FluentReduce] for the term shape, built by [Dot]) into the object/type `Ref` its final segment
 * is a property of, and that final segment's name `Property` -- reducing every access before the
 * last one exactly like [FluentReduce] does. This is what lets the third clause of `:=`/2
 * ([it.unibo.tuprolog.solve.libs.oop.rules.ColonEquals.Assignment]) turn `Obj.foo(1).name := V`
 * into "invoke `foo(1)` on `Obj`, then assign `V` to `name` on the result".
 *
 * ```prolog
 * property_reduce([A, B | C], O, P) :- !, invoke_method(A, B, B1), property_reduce([B1 | C], O, P). % recursive
 * property_reduce([A | B], A, B) :- !.                                                              % base
 * ```
 *
 * @see FluentReduce
 * @see it.unibo.tuprolog.solve.libs.oop.rules.ColonEquals.Assignment
 */
@Suppress("PropertyName")
sealed class PropertyReduce : RuleWrapper<ExecutionContext>(FUNCTOR, ARITY) {
    override val help: String =
        """
        `property_reduce(+Expression, ?Ref, ?Property)`
        
        Helper behind the assignment form of `:=/2`: given a chain of member accesses `Receiver.M1....Mn.Property`, evaluates every access but the last one (as `fluent_reduce/2` does, via `invoke_method/3`), unifying `Ref` with the reference reached that way and `Property` with the final access. Deterministic (each clause cuts); fails if `Expression` is not a chain.

        **Examples**

        ```prolog
        ?- new_object('java.lang.StringBuilder', [abc], B), property_reduce(B.reverse.length, R, P), S := R.toString.
        P = length, S = cba.

        ?- property_reduce(foo, R, P).
        no.
        ```
        """.trimIndent()

    companion object {
        const val FUNCTOR = "property_reduce"
        const val ARITY = 3
    }

    protected val A by variables
    protected val B by variables

    override val Scope.body: Term
        get() = atomOf("!")

    object Recursive : PropertyReduce() {
        private val B1 by variables
        private val C by variables
        private val O by variables
        private val P by variables

        override val Scope.head: List<Term>
            get() = listOf(logicListFrom(A, B, last = C), O, P)

        override val Scope.body: Term
            get() =
                tupleOf(
                    atomOf("!"),
                    structOf(InvokeMethod.functor, A, B, B1),
                    structOf(FUNCTOR, consOf(B1, C), O, P),
                )
    }

    object Base : PropertyReduce() {
        override val Scope.head: List<Term>
            get() = listOf(consOf(A, B), A, B)
    }
}
